import { ReactNode, useEffect, useRef, useState } from 'react'
import Lottie from 'lottie-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import useDarkMode from '@/hooks/useDarkMode'

type LottieOption = {
  loop: boolean
  autoPlay?: boolean
  animationData: object
  width: number
  height: number
  rendererSettings: {
    preserveAspectRatio: string
  }
}

type OutlinedButtonProps = {
  bgColor?: string
  onClick?: (e: any) => void
  onDoubleClick?: () => void
  Icon?: any
  isFocused?: boolean
  tooltip?: string
  badge?: string | number
  lottieOption?: LottieOption | null
  renderRightComponent?: () => ReactNode
  disabled?: boolean
  large?: boolean
  btnId?: string
  color?: string
  focusIconColor?: string
  isRequestProcessing?: boolean
  borderColor?: string
  buttonText?: string
}

export const OutlinedButton: React.FC<OutlinedButtonProps> = ({
  bgColor,
  onClick,
  onDoubleClick,
  Icon,
  isFocused = false,
  tooltip = '',
  badge,
  lottieOption,
  renderRightComponent,
  disabled = false,
  large = false,
  btnId,
  color,
  focusIconColor,
  isRequestProcessing = false,
  borderColor,
  buttonText,
}) => {
  const [mouseOver, setMouseOver] = useState(false)
  const [mouseDown, setMouseDown] = useState(false)
  const [blinkingState, setBlinkingState] = useState(1)
  const isDark = useDarkMode()

  const btnRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const iconSize = 24 * (large ? 1.7 : 1)
  const defaultIconBg = isDark ? 'white' : '#6b7280'

  useEffect(() => {
    if (isRequestProcessing) {
      intervalRef.current = setInterval(() => {
        setBlinkingState((prev) => (prev === 1 ? 0.4 : 1))
      }, 600)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
      setBlinkingState(1)
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [isRequestProcessing])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          ref={btnRef}
          onMouseEnter={() => setMouseOver(true)}
          onMouseLeave={() => setMouseOver(false)}
          onMouseDown={() => setMouseDown(true)}
          onMouseUp={() => setMouseDown(false)}
        >
          <div
            className={`flex items-center justify-center rounded-lg border-2 m-1 ${
              isFocused ? 'bg-white' : bgColor || 'bg-gray-200 dark:bg-transparent'
            } ${borderColor ? `border-${borderColor}` : 'border-gray-900'}`}
            style={{
              transition: 'all 200ms ease-in-out',
              opacity: disabled ? 0.5 : blinkingState,
            }}
          >
            <button
              className={`${
                disabled ? 'cursor-default' : 'cursor-pointer'
              } flex items-center justify-center`}
              id={btnId}
              disabled={disabled}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
            >
              <div
                className="flex items-center justify-center p-1 m-1 rounded-lg"
                style={{
                  transform: `scale(${mouseOver ? (mouseDown ? 0.95 : 1.1) : 1})`,
                  transition: 'all 200ms linear',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                {Icon &&
                  (lottieOption ? (
                    <div className="flex items-center justify-center">
                      <div
                        style={{
                          height: iconSize,
                          width: (iconSize * lottieOption.width) / lottieOption.height,
                        }}
                      >
                        <Lottie
                          loop={lottieOption.loop}
                          autoPlay={lottieOption.autoPlay}
                          animationData={lottieOption.animationData}
                          rendererSettings={{
                            preserveAspectRatio: lottieOption.rendererSettings.preserveAspectRatio,
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <Icon
                        style={{
                          color: isFocused
                            ? focusIconColor || defaultIconBg
                            : color || defaultIconBg,
                          height: iconSize,
                          width: iconSize,
                        }}
                        fillcolor={
                          isFocused ? focusIconColor || defaultIconBg : color || defaultIconBg
                        }
                      />
                      {badge && (
                        <p
                          className="text-base ml-2"
                          style={{
                            color: isFocused ? focusIconColor : defaultIconBg,
                          }}
                        >
                          {badge}
                        </p>
                      )}
                    </>
                  ))}
              </div>
              {buttonText && (
                <p className="text-sm dark:text-white font-semibold mr-2 text-center">
                  {buttonText}
                </p>
              )}
            </button>
            {renderRightComponent?.()}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}
